import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeys } from 'api/config';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';

import { IChallenge } from 'features/challenge/config/types';
import { useUserId } from 'features/user/hooks/useUserId';

type MutationContext = {
  previousChallengeBacklogValue?: { items: IChallenge[] };
};

export const useDeletePlanningBacklogChallengesMutation = (): UseMutationResult<
  any,
  Error,
  string[],
  MutationContext | undefined
> => {
  const { request } = useMutationHTTPRequest();
  const queryClient = useQueryClient();
  const userId = useUserId();

  return useMutation<any, Error, string[], MutationContext | undefined>(
    async (challengeIds: string[]) =>
      await request({
        url: `/api/backlog/challenges/remove`,
        body: { challengeIds, targetUserId: userId },
      }),
    {
      onMutate: async (challengeIds: string[]) => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.backlog, userId],
        });

        const previousChallengeBacklogValue = queryClient.getQueryData<{
          items: IChallenge[];
        }>([queryKeys.backlog, userId]);

        if (previousChallengeBacklogValue) {
          queryClient.setQueryData<{ items: IChallenge[] }>(
            [queryKeys.backlog, userId],
            (oldVal) => ({
              ...oldVal,
              items: oldVal!.items.filter(
                (challenge) => !challengeIds.includes(challenge.challengeId!)
              ),
            })
          );
        }

        return { previousChallengeBacklogValue };
      },
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.backlog, userId]);
      },
      onError: (err: Error, _: string[], context?: MutationContext) => {
        if (context?.previousChallengeBacklogValue) {
          queryClient.setQueryData<{ items: IChallenge[] }>(
            [queryKeys.backlog, userId],
            context.previousChallengeBacklogValue
          );
        }
      },
    }
  );
};
